import { Helmet } from "react-helmet-async";
import { useLanguage } from "@/contexts/language-context";
import { motion } from "framer-motion";

export default function AboutPage() {
  const { t, language } = useLanguage();

  const content = {
    en: {
      title: "CHANGAN KUAYUE HONG KONG",
      paragraphs: [
        "Changan Kuayue's Exclusive Hong Kong Agent. We specialise in providing high-quality MPV and light commercial vehicles tailored for Hong Kong's road conditions, business needs and family usage.",
        "Our core product range includes 7-seater private cars ideal for family, shuttle and daily commuting, as well as 5-seater LGV (Light Goods Vehicles) designed for business delivery, contractors, SMEs and multi-purpose transportation.",
        "As the authorised sole agent, we guarantee genuine vehicles, full manufacturer support, transparent pricing and professional after-sales services. We are committed to delivering reliable, durable and cost-effective mobility solutions with trusted service standards for both private and commercial users across Hong Kong."
      ]
    },
    "zh-TW": {
      title: "長安跨越香港",
      paragraphs: [
        "長安跨越香港獨家代理。我們專注於提供高品質的 MPV 和輕型商用車，以滿足香港的路況、商業需求和家庭用車需求。",
        "我們的核心產品系列包括適合家庭、通勤和日常通勤的 7 人座私家車，以及專為商業配送、承包商、中小企業和多用途運輸而設計的 5 人座輕型貨車 (LGV)。",
        "作為授權獨家代理商，我們保證所有車輛均為正品，並提供全面的廠商支援、透明的價格和專業的售後服務。我們致力於為香港的私人和商業用戶提供可靠、耐用且經濟高效的出行解決方案，並秉承值得信賴的服務標準。"
      ]
    }
  };

  const currentContent = content[language as keyof typeof content] || content["zh-TW"];

  return (
    <>
      <Helmet>
        <title>{language === "en" ? "About Us | 長安跨越 Electric Vehicles" : "關於我們 | 長安跨越 電動車"}</title>
        <meta name="description" content={currentContent.paragraphs[0]} />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl mx-auto text-center"
            >
              <h1 className="text-5xl font-bold mb-6">{t("nav.about")}</h1>
              <p className="text-xl text-blue-100">
                {language === "en" 
                  ? "Official and Exclusive Distributor in Hong Kong" 
                  : "香港官方獨家經銷商"}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-white rounded-2xl shadow-lg p-8 md:p-12"
              >
                <h2 className="text-3xl font-bold mb-8 text-gray-900">
                  {currentContent.title}
                </h2>
                
                <div className="space-y-6 text-gray-700 leading-relaxed">
                  {currentContent.paragraphs.map((paragraph, index) => (
                    <motion.p
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                      className="text-lg"
                    >
                      {paragraph}
                    </motion.p>
                  ))}
                </div>
              </motion.div>

              {/* Key Features */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12"
              >
                {[
                  {
                    title: language === "en" ? "7-Seater Private Cars" : "7人座私家車",
                    desc: language === "en" 
                      ? "Perfect for families and daily commuting" 
                      : "適合家庭及日常通勤"
                  },
                  {
                    title: language === "en" ? "5-Seater LGV" : "5人座輕型貨車",
                    desc: language === "en" 
                      ? "Ideal for business and logistics" 
                      : "適合商業及物流配送"
                  },
                  {
                    title: language === "en" ? "Genuine Vehicles" : "正品保證",
                    desc: language === "en" 
                      ? "Full manufacturer support" 
                      : "全面廠商支援"
                  },
                  {
                    title: language === "en" ? "Professional Service" : "專業服務",
                    desc: language === "en" 
                      ? "Transparent pricing and after-sales" 
                      : "透明價格及售後服務"
                  }
                ].map((feature, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow"
                  >
                    <h3 className="text-xl font-bold mb-2 text-blue-600">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600">{feature.desc}</p>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
